import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import logoo from '../../assets/logoo.png';
import tripsMenu from '../../data/tripsMenu';
import {
  Menu,
  X,
  MessageSquareText,
  Search,
  Bell,
  Settings,
  User,
  LogOut,
  Wallet as WalletIcon,
  Sun,
  Moon,
  Globe,
} from 'lucide-react';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { db } from '../../config/firebase';
import {
  clearNotifications,
  fetchNotifications,
  selectUnreadCount,
  setNotificationsFromListener,
  setNotificationsLoading,
} from '../../store/notificationsSlice';
import { notificationsFromSnapshot } from '../../utils/notificationUtils';

export function MainNavbar() {
  const [isTripsMenuOpen, setTripsMenuOpen] = useState(false);
  const [isFindMenuOpen, setFindMenuOpen] = useState(false);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setUserMenuOpen] = useState(false);
  const [isLanguageMenuOpen, setLanguageMenuOpen] = useState(false);
  // Initialize activeMode from localStorage or default to 'courier'
  const [activeMode, setActiveMode] = useState(() => {
    const savedMode = localStorage.getItem('flexySwap_activeMode');
    return savedMode === 'flexyRide' ? 'flexyRide' : 'courier';
  });
  const tripsMenuRef = useRef(null);
  const findMenuRef = useRef(null);
  const userMenuRef = useRef(null);
  const languageMenuRef = useRef(null);
  const tripsHoverRef = useRef(null);
  const findHoverRef = useRef(null);
  const notificationsUnsubRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user, logout, initializing } = usePublicAuth();
  const { theme, toggleTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const unreadCount = useSelector(selectUnreadCount);

  const cancelHoverTimeout = ref => {
    if (ref.current) {
      clearTimeout(ref.current);
      ref.current = null;
    }
  };

  const scheduleClose = (ref, setter) => {
    cancelHoverTimeout(ref);
    ref.current = setTimeout(() => setter(false), 120);
  };

  // Ensure Flexy Ride pages always show Flexy Ride mode selected in navbar
  // But preserve user's mode choice when navigating to other pages
  useEffect(() => {
    if (location.pathname.startsWith('/flexy-ride')) {
      setActiveMode('flexyRide');
      localStorage.setItem('flexySwap_activeMode', 'flexyRide');
    }
    // Note: We don't override mode on other pages to preserve user's choice
    // The initial state already loads from localStorage
  }, [location.pathname]);

  // Save mode to localStorage when it changes and notify other components
  useEffect(() => {
    if (activeMode) {
      localStorage.setItem('flexySwap_activeMode', activeMode);
      // Dispatch custom event for same-window updates
      window.dispatchEvent(new Event('flexySwap_modeChange'));
    }
  }, [activeMode]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        tripsMenuRef.current &&
        !tripsMenuRef.current.contains(event.target)
      ) {
        setTripsMenuOpen(false);
      }
      if (findMenuRef.current && !findMenuRef.current.contains(event.target)) {
        setFindMenuOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      if (
        languageMenuRef.current &&
        !languageMenuRef.current.contains(event.target)
      ) {
        setLanguageMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = lng => {
    i18n.changeLanguage(lng);
    setLanguageMenuOpen(false);
  };

  useEffect(() => {
    if (initializing) return;

    if (!user?.uid) {
      dispatch(clearNotifications());
      if (notificationsUnsubRef.current) {
        notificationsUnsubRef.current();
        notificationsUnsubRef.current = null;
      }
      return;
    }

    dispatch(fetchNotifications(user.uid));
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
    );
    dispatch(setNotificationsLoading(true));
    const unsubscribe = onSnapshot(
      notificationsQuery,
      snapshot => {
        const mapped = notificationsFromSnapshot(snapshot);
        dispatch(setNotificationsFromListener(mapped));
      },
      () => dispatch(setNotificationsLoading(false)),
    );
    notificationsUnsubRef.current = unsubscribe;

    return () => {
      if (notificationsUnsubRef.current) {
        notificationsUnsubRef.current();
        notificationsUnsubRef.current = null;
      }
    };
  }, [dispatch, user?.uid, initializing]);

  const isGuest = !user && !initializing;

  const scrollToSection = id => {
    const performScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    if (window.location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } });
    } else {
      performScroll();
    }
  };

  const handleGuestNavClick = id => {
    scrollToSection(id);
    setMobileMenuOpen(false);
  };

  const guestNavLinks = [
    { label: 'About', id: 'about-flexyswap' },
    { label: 'Problem', id: 'problem-section' },
    { label: 'How it works', id: 'how-it-works' },
    { label: 'Features', id: 'core-features' },
  ];

  const guestPrimaryLinks = (
    <>
      {guestNavLinks.map(link => (
        <button
          key={link.id}
          className="rounded-full border border-black/10 bg-black px-4 py-2 text-sm font-semibold text-yellow-400 transition hover:bg-black/90 dark:border dark:border-white/15 dark:bg-transparent dark:text-yellow-400 dark:hover:bg-white/10"
          onClick={() => handleGuestNavClick(link.id)}
        >
          {link.label}
        </button>
      ))}
    </>
  );

  const authPrimaryLinks = (
    <>
      <div
        className="relative"
        ref={tripsMenuRef}
        onMouseEnter={() => {
          cancelHoverTimeout(tripsHoverRef);
          setTripsMenuOpen(true);
        }}
        onMouseLeave={() => scheduleClose(tripsHoverRef, setTripsMenuOpen)}
      >
        <button className="flex items-center gap-2 rounded-full bg-black px-4 py-2 text-sm font-semibold text-yellow-400 transition hover:bg-black/90 dark:border dark:border-white/10 dark:bg-transparent dark:text-yellow-400 dark:hover:bg-white/10">
          {t('nav.trips')}
          <span className="text-xs">{isTripsMenuOpen ? '▲' : '▼'}</span>
        </button>
        <div
          className={`absolute left-1/2 top-full mt-4 w-[520px] -translate-x-1/2 rounded-3xl border border-white/10 bg-black/95 p-6 shadow-2xl transition duration-150 ${
            isTripsMenuOpen
              ? 'pointer-events-auto opacity-100'
              : 'pointer-events-none opacity-0'
          }`}
          onMouseEnter={() => {
            cancelHoverTimeout(tripsHoverRef);
            setTripsMenuOpen(true);
          }}
          onMouseLeave={() => scheduleClose(tripsHoverRef, setTripsMenuOpen)}
        >
          {activeMode === 'flexyRide' ? (
            <div className="grid gap-6 lg:grid-cols-2">
              {[
                {
                  section: 'driver',
                  title: t('nav.driver'),
                  items: [
                    {
                      title: t('nav.mySeatListings'),
                      description: t('nav.viewAndManageYourSeatListings'),
                      path: '/flexy-ride/my-seat-listings',
                    },
                    {
                      title: t('nav.myRequests'),
                      description: t('nav.viewYourSeatRequests'),
                      path: '/flexy-ride/my-requests',
                    },
                    {
                      title: t('nav.createSeatListing'),
                      description: t('nav.listANewSeat'),
                      path: '/flexy-ride/create-seat',
                    },
                  ],
                },
                {
                  section: 'passenger',
                  title: t('nav.passenger'),
                  items: [
                    {
                      title: t('nav.myPassengerListings'),
                      description: t('nav.viewAndManageYourPassengerListings'),
                      path: '/flexy-ride/my-passenger-listings',
                    },
                    {
                      title: t('nav.myApplications'),
                      description: t('nav.viewYourPassengerApplications'),
                      path: '/flexy-ride/my-applications',
                    },
                    {
                      title: t('nav.createPassengerListing'),
                      description: t('nav.createANewPassengerListing'),
                      path: '/flexy-ride/create-passenger',
                    },
                  ],
                },
              ].map(({ section, title, items }) => (
                <div key={section}>
                  <p className="text-xs font-semibold uppercase tracking-[0.4em] text-yellow-400">
                    {title}
                  </p>
                  <div className="mt-3 space-y-3">
                    {items.map(item => (
                      <button
                        key={item.title}
                        className="w-full rounded-2xl border border-white/5 bg-white/5 p-3 text-left transition hover:bg-white/10"
                        onClick={() => {
                          navigate(item.path);
                          setTripsMenuOpen(false);
                        }}
                      >
                        <p className="text-sm font-semibold text-white">
                          {item.title}
                        </p>
                        <p className="text-xs text-white/60">
                          {item.description}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid gap-6 lg:grid-cols-2">
              {['sender', 'traveler'].map(section => (
                <div key={section}>
                  <p className="text-xs font-semibold uppercase tracking-[0.4em] text-yellow-400">
                    {section === 'sender'
                      ? t('nav.asSender')
                      : t('nav.asTraveler')}
                  </p>
                  <div className="mt-3 space-y-3">
                    {tripsMenu[section].map(item => (
                      <button
                        key={item.title}
                        className="w-full rounded-2xl border border-white/5 bg-white/5 p-3 text-left transition hover:bg-white/10"
                        onClick={() => {
                          if (item.title === 'Request Delivery') {
                            navigate('/request-delivery');
                          } else if (item.title === 'List New Trip') {
                            navigate('/list-new-trip');
                          } else if (item.title === 'Incoming Requests') {
                            navigate('/incoming-requests');
                          } else if (item.title === 'My Requests') {
                            if (section === 'sender') {
                              navigate('/my-requests');
                            } else if (section === 'traveler') {
                              navigate('/traveler/my-requests');
                            }
                          } else if (item.title === 'My Listings') {
                            navigate('/my-listings');
                          }
                          setTripsMenuOpen(false);
                        }}
                      >
                        <p className="text-sm font-semibold text-white">
                          {item.title}
                        </p>
                        <p className="text-xs text-white/60">
                          {item.description}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div
        className="relative"
        ref={findMenuRef}
        onMouseEnter={() => {
          cancelHoverTimeout(findHoverRef);
          setFindMenuOpen(true);
        }}
        onMouseLeave={() => scheduleClose(findHoverRef, setFindMenuOpen)}
      >
        <button className="flex items-center gap-2 rounded-full bg-black px-4 py-2 text-sm font-semibold text-yellow-400 transition hover:bg-black/90 dark:border dark:border-white/10 dark:bg-transparent dark:text-yellow-400 dark:hover:bg-white/10">
          <Search className="h-4 w-4" />
          {t('nav.findTrips')}
          <span className="text-xs">{isFindMenuOpen ? '▲' : '▼'}</span>
        </button>
        <div
          className={`absolute left-1/2 top-full mt-2 w-80 -translate-x-1/2 rounded-2xl border border-white/10 bg-black/95 p-4 shadow-2xl transition duration-150 ${
            isFindMenuOpen
              ? 'pointer-events-auto opacity-100'
              : 'pointer-events-none opacity-0'
          }`}
          onMouseEnter={() => {
            cancelHoverTimeout(findHoverRef);
            setFindMenuOpen(true);
          }}
          onMouseLeave={() => scheduleClose(findHoverRef, setFindMenuOpen)}
        >
          {activeMode === 'courier' ? (
            <div className="flex gap-3">
              <button
                className="flex-1 rounded-xl border border-white/5 bg-white/5 px-4 py-3 text-left text-white transition hover:bg-white/10"
                onClick={() => {
                  setFindMenuOpen(false);
                  navigate('/trips/sender');
                }}
              >
                <span className="text-sm font-semibold">
                  {t('nav.imASender')}
                </span>
                <span className="mt-1 block text-xs text-white/70">
                  {t('nav.searchTravelersSendPackages')}
                </span>
              </button>
              <button
                className="flex-1 rounded-xl border border-white/5 bg-white/5 px-4 py-3 text-left text-white transition hover:bg-white/10"
                onClick={() => {
                  setFindMenuOpen(false);
                  navigate('/trips/traveler');
                }}
              >
                <span className="text-sm font-semibold">
                  {t('nav.imATraveler')}
                </span>
                <span className="mt-1 block text-xs text-white/70">
                  {t('nav.listTripsEarnMoney')}
                </span>
              </button>
            </div>
          ) : (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-yellow-400">
                {t('nav.flexyRide')}
              </p>
              <div className="mt-3 flex gap-3">
                <button
                  className="flex-1 rounded-xl border border-white/5 bg-white/5 px-4 py-3 text-left text-white transition hover:bg-white/10"
                  onClick={() => {
                    setFindMenuOpen(false);
                    navigate('/flexy-ride/driver');
                  }}
                >
                  <span className="text-sm font-semibold">
                    {t('nav.imAPassenger')}
                  </span>
                  <span className="mt-1 block text-xs text-white/70">
                    {t('nav.seeDriverSeatListings')}
                  </span>
                </button>
                <button
                  className="flex-1 rounded-xl border border-white/5 bg-white/5 px-4 py-3 text-left text-white transition hover:bg-white/10"
                  onClick={() => {
                    setFindMenuOpen(false);
                    navigate('/flexy-ride/passenger');
                  }}
                >
                  <span className="text-sm font-semibold">
                    {t('nav.imADriver')}
                  </span>
                  <span className="mt-1 block text-xs text-white/70">
                    {t('nav.seePassengerRideRequests')}
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <button
        onClick={() => navigate('/chat')}
        className="flex items-center gap-2 rounded-full bg-black px-4 py-2 text-sm font-semibold text-yellow-400 transition hover:bg-black/90 dark:border dark:border-white/10 dark:bg-transparent dark:text-yellow-400 dark:hover:bg-white/10"
      >
        <MessageSquareText className="h-4 w-4" />
        {t('nav.chat')}
      </button>
    </>
  );

  const renderDesktopPrimaryLinks = () => (
    <div className="hidden flex-1 items-center justify-center gap-4 lg:flex">
      {isGuest ? guestPrimaryLinks : authPrimaryLinks}
    </div>
  );

  const displayName =
    user?.displayName ||
    user?.profile?.displayName ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.email?.split('@')[0] ||
    'Flexy user';

  const initials = displayName
    .split(' ')
    .map(part => part.trim()[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const userPhoto =
    user?.photoURL ||
    user?.profile?.photoURL ||
    user?.avatar ||
    user?.photo ||
    null;

  const renderDesktopUserArea = () => {
    if (user) {
      return (
        <div
          className="relative hidden items-center gap-2 rounded-full border border-yellow-400/30 bg-black px-3 py-1.5 text-xs font-semibold text-yellow-400 shadow-sm dark:border dark:border-white/10 dark:bg-white/5 lg:flex"
          ref={userMenuRef}
        >
          <button
            className="flex items-center gap-3 rounded-full bg-black px-3 py-1.5 transition hover:bg-black/90 dark:bg-white/10"
            onClick={() => setUserMenuOpen(prev => !prev)}
          >
            <div className="text-right leading-tight">
              <p className="text-sm font-bold text-yellow-400 truncate max-w-[150px] dark:text-white">
                {displayName}
              </p>
              <p className="text-xs text-yellow-400/80 dark:text-white/60">
                {t('nav.welcomeBack')}
              </p>
            </div>
            {userPhoto ? (
              <img
                src={userPhoto}
                alt={displayName}
                className="h-10 w-10 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-sm font-bold text-white dark:bg-white/20 dark:text-white">
                {initials}
              </div>
            )}
            <span className="text-xs text-yellow-400 dark:text-white">▼</span>
          </button>

          {isUserMenuOpen && (
            <div className="absolute right-0 top-full mt-3 w-60 rounded-3xl border border-white/10 bg-black/95 text-white shadow-2xl">
              <div className="px-4 py-3">
                <p className="text-sm font-semibold text-yellow-400">
                  {displayName}
                </p>
                <p className="text-xs text-white/60">{t('nav.welcomeBack')}</p>
              </div>
              <div className="divide-y divide-white/10">
                <button
                  className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-semibold transition hover:bg-white/5"
                  onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/profile');
                  }}
                >
                  <User className="h-4 w-4" />
                  {t('nav.profile')}
                </button>
                <button
                  className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-semibold transition hover:bg-white/5"
                  onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/wallet');
                  }}
                >
                  <WalletIcon className="h-4 w-4" />
                  {t('nav.wallet')}
                </button>
                <button
                  className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-semibold transition hover:bg-white/5"
                  onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/notifications');
                  }}
                >
                  <div className="relative">
                    <Bell className="h-4 w-4" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-2 -right-3 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-yellow-400 px-1 text-[10px] font-black text-black">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </div>
                  {t('nav.notifications')}
                </button>
                <button
                  className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-semibold transition hover:bg-white/5"
                  onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/settings');
                  }}
                >
                  <Settings className="h-4 w-4" />
                  {t('nav.settings')}
                </button>
              </div>
              <div className="px-4 py-3">
                <button
                  className="flex w-full items-center justify-between rounded-2xl border border-white/10 px-4 py-2 text-sm font-semibold text-yellow-400 transition hover:bg-white/5"
                  onClick={() => {
                    setUserMenuOpen(false);
                    logout();
                  }}
                >
                  <span className="flex items-center gap-2">
                    <LogOut className="h-4 w-4" />
                    {t('nav.logout')}
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (initializing) {
      return (
        <div className="hidden h-6 w-32 animate-pulse rounded-full bg-white/10 lg:block" />
      );
    }

    return (
      <div className="hidden items-center gap-2 lg:flex">
        <button
          onClick={() => navigate('/auth/login')}
          className="rounded-full bg-black px-4 py-2 text-sm font-semibold text-yellow-400 transition hover:bg-black/90 dark:border dark:border-white/20 dark:bg-transparent dark:text-yellow-400 dark:hover:bg-white/10"
        >
          {t('auth.signIn')}
        </button>
        <button
          onClick={() => navigate('/auth/login?mode=signup')}
          className="rounded-full bg-black px-4 py-2 text-sm font-semibold text-yellow-400 transition hover:bg-black/90 dark:bg-yellow-400 dark:text-black dark:hover:bg-yellow-300"
        >
          {t('auth.signUp')}
        </button>
      </div>
    );
  };

  const renderMobileMenu = () => (
    <div className="border-t border-gray-200 bg-white/95 text-gray-900 dark:border-white/10 dark:bg-black/95 dark:text-white lg:hidden">
      <div className="max-h-[calc(100vh-80px)] overflow-y-auto px-4 py-6 space-y-6">
        {user && (
          <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/5">
            {userPhoto ? (
              <img
                src={userPhoto}
                alt={displayName}
                className="h-12 w-12 rounded-full object-cover border border-gray-200 dark:border-white/20"
              />
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black/10 text-base font-bold text-gray-900 dark:bg-white/10 dark:text-white">
                {initials}
              </div>
            )}
            <div>
              <p className="text-[11px] uppercase tracking-[0.4em] text-gray-500 dark:text-white/60">
                {t('nav.welcomeBack')}
              </p>
              <p className="text-base font-bold text-gray-900 dark:text-white">
                {displayName}
              </p>
            </div>
          </div>
        )}

        {/* Mobile mode switch (Flexy Courier / Flexy Ride) */}
        <div className="flex items-center justify-between rounded-full border border-gray-200 bg-gray-50 px-2 py-1.5 text-sm font-semibold dark:border-white/10 dark:bg-white/5">
          <button
            type="button"
            className={`flex-1 rounded-full px-3 py-1 text-xs font-extrabold ${
              activeMode === 'courier'
                ? 'bg-yellow-400 text-black shadow-sm'
                : 'bg-transparent text-gray-800 hover:bg-white/40 dark:text-white'
            }`}
            onClick={() => {
              setActiveMode('courier');
              localStorage.setItem('flexySwap_activeMode', 'courier');
            }}
          >
            {t('nav.flexyCourie')}
          </button>
          <button
            type="button"
            className={`ml-1 flex-1 rounded-full px-3 py-1 text-xs font-extrabold ${
              activeMode === 'flexyRide'
                ? 'bg-yellow-400 text-black shadow-sm'
                : 'bg-transparent text-gray-800 hover:bg-white/40 dark:text-white'
            }`}
            onClick={() => {
              setActiveMode('flexyRide');
              localStorage.setItem('flexySwap_activeMode', 'flexyRide');
            }}
          >
            {t('nav.flexyRide')}
          </button>
        </div>
        <div className="flex flex-col gap-3">
          {isGuest ? (
            guestPrimaryLinks
          ) : (
            <>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-yellow-400">
                {t('nav.trips')}
              </p>
              {activeMode === 'flexyRide' ? (
                <div className="grid gap-3">
                  {[
                    {
                      section: 'driver',
                      title: t('nav.driver'),
                      items: [
                        {
                          title: t('nav.mySeatListings'),
                          description: t('nav.viewAndManageYourSeatListings'),
                          path: '/flexy-ride/my-seat-listings',
                        },
                        {
                          title: t('nav.myRequests'),
                          description: t('nav.viewYourSeatRequests'),
                          path: '/flexy-ride/my-requests',
                        },
                        {
                          title: t('nav.createSeatListing'),
                          description: t('nav.listANewSeat'),
                          path: '/flexy-ride/create-seat',
                        },
                      ],
                    },
                    {
                      section: 'passenger',
                      title: t('nav.passenger'),
                      items: [
                        {
                          title: t('nav.myPassengerListings'),
                          description: t(
                            'nav.viewAndManageYourPassengerListings',
                          ),
                          path: '/flexy-ride/my-passenger-listings',
                        },
                        {
                          title: t('nav.myApplications'),
                          description: t('nav.viewYourPassengerApplications'),
                          path: '/flexy-ride/my-applications',
                        },
                        {
                          title: t('nav.createPassengerListing'),
                          description: t('nav.createANewPassengerListing'),
                          path: '/flexy-ride/create-passenger',
                        },
                      ],
                    },
                  ].map(({ section, title, items }) => (
                    <div key={`mobile-${section}`}>
                      <p className="text-[11px] uppercase tracking-[0.3em] text-gray-500 dark:text-white/60">
                        {title}
                      </p>
                      <div className="mt-2 flex flex-col gap-2">
                        {items.map(item => (
                          <button
                            key={`mobile-${item.title}`}
                            className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-left dark:border-white/10 dark:bg-white/5"
                            onClick={() => {
                              navigate(item.path);
                              setMobileMenuOpen(false);
                            }}
                          >
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">
                              {item.title}
                            </p>
                            <p className="text-xs text-gray-600 dark:text-white/60">
                              {item.description}
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div className="grid gap-3">
                    {['sender', 'traveler'].map(section => (
                      <div key={`mobile-${section}`}>
                        <p className="text-[11px] uppercase tracking-[0.3em] text-gray-500 dark:text-white/60">
                          {section === 'sender'
                            ? t('nav.asSender')
                            : t('nav.asTraveler')}
                        </p>
                        <div className="mt-2 flex flex-col gap-2">
                          {tripsMenu[section].map(item => (
                            <button
                              key={`mobile-${item.title}`}
                              className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-left dark:border-white/10 dark:bg-white/5"
                              onClick={() => {
                                if (item.title === 'Request Delivery') {
                                  navigate('/request-delivery');
                                } else if (item.title === 'List New Trip') {
                                  navigate('/list-new-trip');
                                } else if (item.title === 'Incoming Requests') {
                                  navigate('/incoming-requests');
                                } else if (item.title === 'My Requests') {
                                  if (section === 'sender') {
                                    navigate('/my-requests');
                                  } else if (section === 'traveler') {
                                    navigate('/traveler/my-requests');
                                  }
                                } else if (item.title === 'My Listings') {
                                  navigate('/my-listings');
                                }
                                setMobileMenuOpen(false);
                              }}
                            >
                              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                {item.title}
                              </p>
                              <p className="text-xs text-gray-600 dark:text-white/60">
                                {item.description}
                              </p>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      navigate('/trips/sender');
                      setMobileMenuOpen(false);
                    }}
                    className="rounded-2xl border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-yellow-500 dark:border-white/10"
                  >
                    {t('nav.browseAsSender')}
                  </button>
                  <button
                    onClick={() => {
                      navigate('/trips/traveler');
                      setMobileMenuOpen(false);
                    }}
                    className="rounded-2xl border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-yellow-500 dark:border-white/10"
                  >
                    {t('nav.browseAsTraveler')}
                  </button>
                </>
              )}
              {activeMode === 'flexyRide' && (
                <>
                  <button
                    onClick={() => {
                      navigate('/flexy-ride/driver');
                      setMobileMenuOpen(false);
                    }}
                    className="rounded-2xl border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-yellow-500 dark:border-white/10"
                  >
                    {t('nav.browseAsDriver')}
                  </button>
                  <button
                    onClick={() => {
                      navigate('/flexy-ride/passenger');
                      setMobileMenuOpen(false);
                    }}
                    className="rounded-2xl border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-yellow-500 dark:border-white/10"
                  >
                    {t('nav.browseAsPassenger')}
                  </button>
                </>
              )}
              <button
                onClick={() => {
                  navigate('/chat');
                  setMobileMenuOpen(false);
                }}
                className="flex items-center gap-2 rounded-2xl border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-yellow-500 dark:border-white/10"
              >
                <MessageSquareText className="h-4 w-4" />
                {t('nav.chat')}
              </button>
              <button
                onClick={() => {
                  navigate('/wallet');
                  setMobileMenuOpen(false);
                }}
                className="flex items-center gap-2 rounded-2xl border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-yellow-500 dark:border-white/10"
              >
                <WalletIcon className="h-4 w-4" />
                {t('nav.wallet')}
              </button>
            </>
          )}
        </div>
        <div className="border-t border-gray-200 pt-6 dark:border-white/10">
          {user ? (
            <div className="flex flex-col gap-3">
              <button
                className="flex items-center gap-2 rounded-2xl border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-yellow-500 dark:border-white/10"
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate('/notifications');
                }}
              >
                <Bell className="h-4 w-4" />
                {t('nav.notifications')} ({unreadCount})
              </button>
              <button
                className="flex items-center gap-2 rounded-2xl border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-yellow-500 dark:border-white/10"
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate('/profile');
                }}
              >
                <User className="h-4 w-4" />
                {t('nav.profile')}
              </button>
              <button
                className="flex items-center gap-2 rounded-2xl border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-yellow-500 dark:border-white/10"
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate('/settings');
                }}
              >
                <Settings className="h-4 w-4" />
                {t('nav.settings')}
              </button>
              <button
                className="flex items-center gap-2 rounded-2xl border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-yellow-500 dark:border-white/10"
                onClick={() => {
                  setMobileMenuOpen(false);
                  logout();
                }}
              >
                <LogOut className="h-4 w-4" />
                {t('nav.logout')}
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  navigate('/auth/login');
                  setMobileMenuOpen(false);
                }}
                className="rounded-2xl border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-yellow-500 transition hover:bg-gray-100 dark:border-white/20 dark:text-yellow-400 dark:hover:bg-white/10"
              >
                {t('auth.signIn')}
              </button>
              <button
                onClick={() => {
                  navigate('/auth/login?mode=signup');
                  setMobileMenuOpen(false);
                }}
                className="rounded-2xl bg-yellow-500 px-4 py-3 text-left text-sm font-semibold text-black transition hover:bg-yellow-400"
              >
                {t('nav.createAccount')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-yellow-500/20 bg-yellow-400 text-black backdrop-blur dark:bg-black/95 dark:text-white dark:bg-slate-950">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 lg:px-6">
        <div className="flex items-center gap-3">
          <button
            className="flex items-center gap-3"
            onClick={() => {
              navigate('/');
              setMobileMenuOpen(false);
            }}
          >
            <img
              src={logoo}
              alt="FlexySwap Logo"
              className="h-12 w-12 object-contain brightness-0 dark:brightness-100"
            />
            <span className="text-lg font-bold tracking-wide text-black dark:text-yellow-400">
              FlexySwap
            </span>
          </button>

          {/* Desktop mode switch */}
          <div className="hidden items-center gap-1 rounded-full border border-yellow-400/40 bg-black/80 px-3 py-1.5 text-sm font-extrabold text-yellow-400 shadow-sm dark:border-white/15 dark:bg-white/5 lg:flex">
            <button
              type="button"
              className={`rounded-full px-5 py-2 text-sm font-extrabold shadow-sm ${
                activeMode === 'courier'
                  ? 'bg-yellow-400 text-black'
                  : 'bg-transparent text-yellow-400 hover:bg-white/5'
              }`}
              onClick={() => {
                setActiveMode('courier');
                localStorage.setItem('flexySwap_activeMode', 'courier');
              }}
            >
              {t('nav.flexyCourie')}
            </button>
            <button
              type="button"
              className={`rounded-full px-5 py-2 text-sm font-extrabold ${
                activeMode === 'flexyRide'
                  ? 'bg-yellow-400 text-black shadow-sm'
                  : 'bg-transparent text-yellow-400 hover:bg-white/5'
              }`}
              onClick={() => {
                setActiveMode('flexyRide');
                localStorage.setItem('flexySwap_activeMode', 'flexyRide');
              }}
            >
              {t('nav.flexyRide')}
            </button>
          </div>
          <button
            type="button"
            className="rounded-full border border-yellow-400/30 bg-black p-2 text-yellow-400 hover:bg-black/90 dark:border dark:border-white/15 dark:bg-transparent dark:text-white dark:hover:bg-white/10 lg:hidden"
            onClick={() => setMobileMenuOpen(prev => !prev)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </button>
        </div>

        {renderDesktopPrimaryLinks()}

        <div className="flex items-center gap-3">
          {renderDesktopUserArea()}

          {/* Language selector */}
          <div className="relative" ref={languageMenuRef}>
            <button
              onClick={() => setLanguageMenuOpen(prev => !prev)}
              className="rounded-full border border-yellow-400/30 bg-black p-2 text-yellow-400 transition hover:bg-black/90 dark:border dark:border-white/10 dark:bg-transparent dark:text-yellow-400 dark:hover:bg-white/10"
              aria-label="Select language"
            >
              <Globe className="h-4 w-4" />
            </button>
            {isLanguageMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-40 rounded-2xl border border-white/10 bg-black/95 p-2 shadow-2xl">
                <button
                  onClick={() => changeLanguage('en')}
                  className={`w-full rounded-xl px-4 py-2 text-left text-sm font-semibold transition ${
                    i18n.language === 'en'
                      ? 'bg-yellow-400/20 text-yellow-400'
                      : 'text-white hover:bg-white/5'
                  }`}
                >
                  English
                </button>
                <button
                  onClick={() => changeLanguage('fr')}
                  className={`w-full rounded-xl px-4 py-2 text-left text-sm font-semibold transition ${
                    i18n.language === 'fr'
                      ? 'bg-yellow-400/20 text-yellow-400'
                      : 'text-white hover:bg-white/5'
                  }`}
                >
                  Français
                </button>
              </div>
            )}
          </div>

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="rounded-full border border-yellow-400/30 bg-black p-2 text-yellow-400 transition hover:bg-black/90 dark:border dark:border-white/10 dark:bg-transparent dark:text-yellow-400 dark:hover:bg-white/10"
            aria-label="Toggle theme"
          >
            {theme === 'light' ? (
              <Moon className="h-4 w-4" />
            ) : (
              <Sun className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && renderMobileMenu()}
    </nav>
  );
}
